import type { ComponentType, SVGProps } from "react";
import Button from "../../../shared/components/Button";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { FiPlus, FiUsers } from "react-icons/fi";
import type {
  DraftPair,
  DraftPlayer,
} from "../../challenges/types/challengeDraft";

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const PlusIcon = FiPlus as unknown as IconLike;
const UsersIcon = FiUsers as unknown as IconLike;

type AdminDrawPairsSummaryProps = {
  canGenerateRounds: boolean;
  canSavePairs?: boolean;
  nameByCpf: Record<string, string>;
  onGenerateRounds: () => void;
  onSavePairs?: () => void | Promise<void>;
  pairs: DraftPair[];
  players?: DraftPlayer[];
  resolveSideLabel?: (side: DraftPlayer["side"]) => string;
  saving?: boolean;
  showGenerateRounds?: boolean;
};

export default function AdminDrawPairsSummary({
  canGenerateRounds,
  canSavePairs = true,
  nameByCpf,
  onGenerateRounds,
  onSavePairs,
  pairs,
  players = [],
  resolveSideLabel,
  saving = false,
  showGenerateRounds = true,
}: AdminDrawPairsSummaryProps) {
  const playerByCpf = new Map(players.map((player) => [player.cpf, player]));
  const resolvePlayerName = (cpf: string, fallback?: string) =>
    nameByCpf[cpf] ||
    playerByCpf.get(cpf)?.name ||
    fallback ||
    siteTexts.challengeEngine.labels.player;
  const resolvePlayerSide = (cpf: string) => {
    const player = playerByCpf.get(cpf);
    if (!player?.side) {
      return siteTexts.challengeEngine.labels.sidePreferenceMissing;
    }
    return resolveSideLabel
      ? resolveSideLabel(player.side)
      : player.side.direita && player.side.esquerda
        ? siteTexts.challengeEngine.labels.rightAndLeft
        : player.side.direita
          ? siteTexts.challengeEngine.labels.right
          : siteTexts.challengeEngine.labels.left;
  };

  return (
    <details
      className="lista-duplas lista-duplas-collapsible admin-draw-pairs-summary"
      open
    >
      <summary>
        <span className="admin-draw-pairs-summary-heading">
          <span>{siteTexts.challengeEngine.labels.drawedPairs}</span>
          <small>{siteTexts.challengeEngine.labels.drawedPairsHint}</small>
        </span>
      </summary>
      {pairs.length === 0 ? (
        <p className="vazio">{siteTexts.challengeEngine.labels.nonePairDrawn}</p>
      ) : (
        <div className="admin-draw-pairs-list">
          {pairs.map((pair, index) => (
            <article
              key={pair.id}
              className="admin-draw-pair-card"
              aria-label={`${siteTexts.challengeEngine.labels.pairNumber} ${
                index + 1
              }`}
            >
              <div className="admin-draw-pair-index">
                <strong>{index + 1}</strong>
                <span>{siteTexts.challengeEngine.labels.pair}</span>
              </div>
              <div className="admin-draw-pair-members">
                <div className="admin-draw-pair-member">
                  <strong>
                    {resolvePlayerName(
                      pair.cpf1,
                      pair.label?.split("/")[0]?.trim(),
                    )}
                  </strong>
                  <small>
                    <span>{siteTexts.challengeEngine.labels.sidePrefix}:</span>
                    <em>{resolvePlayerSide(pair.cpf1)}</em>
                  </small>
                </div>
                <span className="admin-draw-pair-plus" aria-hidden="true">
                  <PlusIcon />
                </span>
                <div className="admin-draw-pair-member">
                  <strong>
                    {resolvePlayerName(
                      pair.cpf2,
                      pair.label?.split("/")[1]?.trim(),
                    )}
                  </strong>
                  <small>
                    <span>{siteTexts.challengeEngine.labels.sidePrefix}:</span>
                    <em>{resolvePlayerSide(pair.cpf2)}</em>
                  </small>
                </div>
              </div>
              <UsersIcon className="admin-draw-pair-icon" aria-hidden="true" />
            </article>
          ))}
        </div>
      )}
      {(onSavePairs || showGenerateRounds) && (
        <div className="admin-draw-pairs-actions">
          {onSavePairs && (
            <Button
              type="button"
              variant="pill"
              disabled={pairs.length === 0 || saving || !canSavePairs}
              onClick={onSavePairs}
            >
              {saving
                ? siteTexts.challengeEngine.labels.savingDrawPairs
                : siteTexts.challengeEngine.labels.saveDrawPairs}
            </Button>
          )}
          {showGenerateRounds && (
            <Button
              type="button"
              variant="pill"
              className="gerar-rodadas"
              disabled={!canGenerateRounds || saving}
              onClick={onGenerateRounds}
            >
              {siteTexts.challengeEngine.labels.generateRounds}
            </Button>
          )}
        </div>
      )}
    </details>
  );
}
